import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useFetch from "frontend/utils/useFetch";
import { humanizedDate } from "frontend/utils/date_util.js";
import "./Admin.scss";

const Admin = () => {
  const history = useHistory();
  const [stats, setStats] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);

  const { doFetch, fetchError } = useFetch("/api/v1/admin/stats", ({ response, json }) => {
    if (response.status === 401 || response.status === 403) {
      setUnauthorized(true);
      return;
    }
    setStats(json);
  });

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  useEffect(() => {
    if (unauthorized || fetchError) {
      history.replace("/");
    }
  }, [unauthorized, fetchError, history]);

  if (!stats) {
    return null;
  }

  const { totals, activity_by_day, ai_matchup, queue, users } = stats;

  return (
    <div className="admin-page">
      <div className="admin-content">
        <h1 className="admin-title">Admin Panel</h1>

        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{totals.total_users}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{totals.total_guests}</div>
            <div className="admin-stat-label">Guests</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{totals.total_games}</div>
            <div className="admin-stat-label">Total Games</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{totals.games_in_progress}</div>
            <div className="admin-stat-label">In Progress</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{totals.games_completed}</div>
            <div className="admin-stat-label">Completed</div>
          </div>
        </div>

        <h2 className="admin-section-title">Waiting for Match</h2>
        <p className="admin-queue-count">{queue.waiting_count} waiting</p>
        {queue.waiting_count > 0 && (
          <ul className="admin-queue-list">
            {queue.entries.map((entry) => (
              <li key={entry.username}>
                {entry.username} — waiting since {humanizedDate(new Date(entry.waiting_since))}
              </li>
            ))}
          </ul>
        )}

        <h2 className="admin-section-title">Activity (last 30 days)</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>New Users</th>
              <th>New Guests</th>
              <th>New Games</th>
            </tr>
          </thead>
          <tbody>
            {activity_by_day.map((day) => (
              <tr key={day.date}>
                <td>{day.date}</td>
                <td>{day.new_users}</td>
                <td>{day.new_guests}</td>
                <td>{day.new_games}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="admin-section-title">AI Matchup</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Difficulty</th>
              <th>Human Wins</th>
              <th>AI Wins</th>
              <th>Ties</th>
            </tr>
          </thead>
          <tbody>
            {ai_matchup.map((row) => (
              <tr key={row.difficulty}>
                <td>{row.difficulty}</td>
                <td>{row.human_wins}</td>
                <td>{row.ai_wins}</td>
                <td>{row.ties}</td>
              </tr>
            ))}
            <tr className="admin-table-total">
              <td>Total</td>
              <td>{ai_matchup.reduce((sum, row) => sum + row.human_wins, 0)}</td>
              <td>{ai_matchup.reduce((sum, row) => sum + row.ai_wins, 0)}</td>
              <td>{ai_matchup.reduce((sum, row) => sum + row.ties, 0)}</td>
            </tr>
          </tbody>
        </table>

        <h2 className="admin-section-title">Users</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Joined</th>
              <th>Games Played</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.username}>
                <td>{user.username}</td>
                <td>{humanizedDate(new Date(user.joined_at))}</td>
                <td>{user.games_played}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
